"use client";

import { useEffect, useRef } from "react";

/**
 * Plays a soft notification sound when unreadCount increases.
 * Minimal change - only enhances frontend when new notification received.
 * Uses Web Audio API (no external file, works on mobile + web).
 * Respects browser autoplay - requires prior user interaction.
 */
export function useNotificationSound(unreadCount: number) {
  const prevCountRef = useRef<number | null>(null);
  const hasInteractedRef = useRef(false);

  useEffect(() => {
    const handleInteraction = () => {
      hasInteractedRef.current = true;
    };

    document.addEventListener("click", handleInteraction, { once: true });
    document.addEventListener("touchstart", handleInteraction, { once: true });
    document.addEventListener("keydown", handleInteraction, { once: true });

    return () => {
      document.removeEventListener("click", handleInteraction);
      document.removeEventListener("touchstart", handleInteraction);
      document.removeEventListener("keydown", handleInteraction);
    };
  }, []);

  useEffect(() => {
    if (!hasInteractedRef.current) return;

    if (prevCountRef.current === null) {
      prevCountRef.current = unreadCount;
      return;
    }

    if (unreadCount <= prevCountRef.current) {
      prevCountRef.current = unreadCount;
      return;
    }

    prevCountRef.current = unreadCount;

    try {
      const AudioContextClass =
        typeof window !== "undefined"
          ? (window.AudioContext || (window as unknown as { webkitAudioContext?: new () => AudioContext }).webkitAudioContext)
          : null;
      if (!AudioContextClass) return;

      const audioContext = new AudioContextClass();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      oscillator.frequency.value = 880;
      oscillator.type = "sine";
      gainNode.gain.setValueAtTime(0.08, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.001, audioContext.currentTime + 0.15);

      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.15);
    } catch {
      // Silently fail - sound is non-critical
    }
  }, [unreadCount]);
}
